/**
 * One rule for reading a ticket's recorded exit.
 *
 * ## Invariants:
 * - An absent exit and an exit of epoch 0 mean the same thing: the parker has not left yet. Why:
 *   the API reports an open ticket's `exit` as `0`, not as null, so a plain null check passes and
 *   the value formats as 1 Jan 1969. Every caller must go through this rule rather than testing
 *   `!= null` itself — that divergence is exactly how the touchless screen and Parking History
 *   ended up disagreeing about the same ticket.
 * - Returns a RECORDED exit only, never a projected one. Why: "paid until" (`rateValidUntil` /
 *   `exitBy`) is computed per-response by the touchless endpoint and is not stored on the ticket,
 *   so it is not this function's to supply — a caller that has it must apply it itself, and one
 *   that does not must not imply an exit that has not happened.
 */
export function resolveRecordedTicketExit(
  exitRaw: string | number | null | undefined
): string | undefined {
  if (exitRaw == null) return undefined;
  const exitDate = new Date(exitRaw);
  const time = exitDate.getTime();
  // NaN for an unparseable value; <= 0 for the open-ticket sentinel.
  if (!Number.isFinite(time) || time <= 0) return undefined;
  return exitDate.toISOString();
}

/**
 * One rule for whether a candidate exit is a real exit: strictly AFTER the entry.
 *
 * ## Invariants:
 * - An exit equal to the entry is absent, not a zero-length stay. Why: it is the shape several
 *   honest computations produce (an unpaid ticket's paid-until, a payment too small to buy the
 *   first increment, `DBTicket.getExit()` clamping an early exit up to the entry).
 * - Returns the candidate UNCHANGED when it qualifies. Why: callers pass values already resolved
 *   by their own chain, and re-serializing would discard whatever offset that chain settled on.
 */
export function exitAfterEntryOrUndefined<T extends string | number>(
  exit: T | null | undefined,
  enter: string | number | null | undefined
): T | undefined {
  if (exit == null || enter == null) return undefined;
  const exitMs = new Date(exit).getTime();
  const enterMs = new Date(enter).getTime();
  if (!Number.isFinite(exitMs) || !Number.isFinite(enterMs)) return undefined;
  return exitMs > enterMs ? exit : undefined;
}
